import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import api from '@/services/apiClient';
import { getSocket } from '@/services/socket';
import EmptyState from '@/components/ui/EmptyState';
import Spinner from '@/components/ui/Spinner';
import styles from './NotificationsBell.module.css';

const timeAgo = (iso, t) => {
  if (!iso) return '';
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const min = Math.floor(diff / 60000);
  if (min < 1) return t('notifications.justNow');
  if (min < 60) return t('notifications.minutesAgo', { count: min });
  const hours = Math.floor(min / 60);
  if (hours < 24) return t('notifications.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('notifications.daysAgo', { count: days });
};

export default function NotificationsBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const unread = items.filter((n) => !n.readAt).length;

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications');
      setItems((res.data || res).notifications || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Carrega a contagem ao montar.
  useEffect(() => {
    load();
  }, []);

  // Tempo real: nova notificação entra no topo (badge atualiza junto).
  useEffect(() => {
    const socket = getSocket();
    const onNew = (n) => setItems((list) => [n, ...list.filter((it) => it.id !== n.id)]);
    socket.on('notification:new', onNew);
    return () => socket.off('notification:new', onNew);
  }, []);

  // Fecha ao clicar fora.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) load();
  };

  const markAllRead = async () => {
    // Otimista.
    const now = new Date().toISOString();
    setItems((list) => list.map((it) => (it.readAt ? it : { ...it, readAt: now })));
    try {
      await api.post('/notifications/read-all', {});
    } catch {
      load();
    }
  };

  const markRead = async (n) => {
    if (n.readAt) return;
    // Otimista.
    setItems((list) =>
      list.map((it) => (it.id === n.id ? { ...it, readAt: new Date().toISOString() } : it))
    );
    try {
      await api.post(`/notifications/${n.id}/read`, {});
    } catch {
      load();
    }
  };

  return (
    <div className={styles.wrap} ref={ref}>
      <button
        type="button"
        className={styles.iconBtn}
        onClick={toggle}
        aria-label={t('notifications.title')}
      >
        <Bell size={18} strokeWidth={2} />
        {unread > 0 && <span className={styles.badge}>{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <div className={styles.panel} role="dialog">
          <div className={styles.header}>
            <span className={styles.headerTitle}>{t('notifications.title')}</span>
            {unread > 0 && (
              <button type="button" className={styles.markAll} onClick={markAllRead}>
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          <div className={styles.list}>
            {loading ? (
              <div className={styles.loading}>
                <Spinner />
              </div>
            ) : items.length === 0 ? (
              <EmptyState icon={Bell} title={t('notifications.empty')} />
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className={`${styles.item} ${n.readAt ? '' : styles.unread}`}
                  onClick={() => markRead(n)}
                >
                  {!n.readAt && <span className={styles.dot} />}
                  <div className={styles.itemBody}>
                    <div className={styles.itemTitle}>{n.title}</div>
                    {n.body && <div className={styles.itemText}>{n.body}</div>}
                    <div className={styles.itemTime}>{timeAgo(n.createdAt, t)}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
